/**
 * DEMO: category PLP product-list fixtures keyed by ICM category path
 * (e.g. `HW`, `HW/PORT`). Used to rebuild productfilters from live product data.
 */
import avProducts from '../../../../assets/mock-data/categories/AV/products/get.json';
import avMonProducts from '../../../../assets/mock-data/categories/AV/MON/products/get.json';
import avTvProducts from '../../../../assets/mock-data/categories/AV/TV/products/get.json';
import cloudsecProducts from '../../../../assets/mock-data/categories/CLOUDSEC/products/get.json';
import cloudsecCloudProducts from '../../../../assets/mock-data/categories/CLOUDSEC/CLOUD/products/get.json';
import cloudsecSecProducts from '../../../../assets/mock-data/categories/CLOUDSEC/SEC/products/get.json';
import hwProducts from '../../../../assets/mock-data/categories/HW/products/get.json';
import hwAccProducts from '../../../../assets/mock-data/categories/HW/ACC/products/get.json';
import hwMemProducts from '../../../../assets/mock-data/categories/HW/MEM/products/get.json';
import hwPortProducts from '../../../../assets/mock-data/categories/HW/PORT/products/get.json';
import hwPwrProducts from '../../../../assets/mock-data/categories/HW/PWR/products/get.json';
import hwStorProducts from '../../../../assets/mock-data/categories/HW/STOR/products/get.json';
import hwSysProducts from '../../../../assets/mock-data/categories/HW/SYS/products/get.json';
import mobileProducts from '../../../../assets/mock-data/categories/MOBILE/products/get.json';
import mobilePhoneProducts from '../../../../assets/mock-data/categories/MOBILE/PHONE/products/get.json';
import netcomProducts from '../../../../assets/mock-data/categories/NETCOM/products/get.json';
import netcomNetProducts from '../../../../assets/mock-data/categories/NETCOM/NET/products/get.json';
import netcomTelProducts from '../../../../assets/mock-data/categories/NETCOM/TEL/products/get.json';
import netcomUccProducts from '../../../../assets/mock-data/categories/NETCOM/UCC/products/get.json';
import officeProducts from '../../../../assets/mock-data/categories/OFFICE/products/get.json';
import officeInputProducts from '../../../../assets/mock-data/categories/OFFICE/INPUT/products/get.json';
import officeMachProducts from '../../../../assets/mock-data/categories/OFFICE/MACH/products/get.json';
import officePrintProducts from '../../../../assets/mock-data/categories/OFFICE/PRINT/products/get.json';
import whProducts from '../../../../assets/mock-data/categories/WH/products/get.json';

import { extractProductListStubSku } from './presidio-mock-discovery-handlers';
import { PRESIDIO_SHOP_DEPARTMENT_IDS } from 'ish-core/utils/presidio-shop-breadcrumb';

export type CategoryProductsFixture = {
  elements?: Array<{
    title?: string;
    description?: string;
    attributes?: Array<{ name?: string; value?: unknown }>;
  }>;
  total?: number;
  type?: string;
  offset?: number;
  amount?: number;
};

const CATEGORY_PRODUCT_LISTS: Record<string, CategoryProductsFixture> = {
  AV: avProducts as CategoryProductsFixture,
  'AV/MON': avMonProducts as CategoryProductsFixture,
  'AV/TV': avTvProducts as CategoryProductsFixture,
  CLOUDSEC: cloudsecProducts as CategoryProductsFixture,
  'CLOUDSEC/CLOUD': cloudsecCloudProducts as CategoryProductsFixture,
  'CLOUDSEC/SEC': cloudsecSecProducts as CategoryProductsFixture,
  HW: hwProducts as CategoryProductsFixture,
  'HW/ACC': hwAccProducts as CategoryProductsFixture,
  'HW/MEM': hwMemProducts as CategoryProductsFixture,
  'HW/PORT': hwPortProducts as CategoryProductsFixture,
  'HW/PWR': hwPwrProducts as CategoryProductsFixture,
  'HW/STOR': hwStorProducts as CategoryProductsFixture,
  'HW/SYS': hwSysProducts as CategoryProductsFixture,
  MOBILE: mobileProducts as CategoryProductsFixture,
  'MOBILE/PHONE': mobilePhoneProducts as CategoryProductsFixture,
  NETCOM: netcomProducts as CategoryProductsFixture,
  'NETCOM/NET': netcomNetProducts as CategoryProductsFixture,
  'NETCOM/TEL': netcomTelProducts as CategoryProductsFixture,
  'NETCOM/UCC': netcomUccProducts as CategoryProductsFixture,
  OFFICE: officeProducts as CategoryProductsFixture,
  'OFFICE/INPUT': officeInputProducts as CategoryProductsFixture,
  'OFFICE/MACH': officeMachProducts as CategoryProductsFixture,
  'OFFICE/PRINT': officePrintProducts as CategoryProductsFixture,
  WH: whProducts as CategoryProductsFixture,
};

/** Aggregate all Shop department catalogs (excludes My Warehouse). */
export function buildShopCatalogProductsFixture(): CategoryProductsFixture {
  const seen = new Set<string>();
  const elements: NonNullable<CategoryProductsFixture['elements']> = [];

  for (const departmentId of PRESIDIO_SHOP_DEPARTMENT_IDS) {
    const fixture = CATEGORY_PRODUCT_LISTS[departmentId];
    for (const el of fixture?.elements || []) {
      const sku = extractProductListStubSku(el) || el.title || '';
      const key = sku.trim().toUpperCase() || JSON.stringify(el);
      if (seen.has(key)) {
        continue;
      }
      seen.add(key);
      elements.push(el);
    }
  }

  return {
    type: 'ResourceCollection',
    elements,
    total: elements.length,
    offset: 1,
    amount: elements.length,
  };
}

/** Resolve category product list for an ICM path like `HW` or `HW/PORT`. */
export function getCategoryProductsFixture(categoryPath: string): CategoryProductsFixture | undefined {
  const normalized = (categoryPath || '').replace(/^\/+|\/+$/g, '');
  if (normalized.toUpperCase() === 'SHOP') {
    return buildShopCatalogProductsFixture();
  }
  return CATEGORY_PRODUCT_LISTS[normalized];
}
